import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Inject, Input, OnChanges, Output, ViewChild, ViewEncapsulation, ElementRef, HostListener, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import csvDownload from 'json-to-csv-export'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SelectionModel } from '@angular/cdk/collections';
import { DOCUMENT } from '@angular/common';
import { IonInfiniteScroll } from '@ionic/angular';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

const SANITIZED_HTML_CACHE_MAX = 5000;

/**
 * Tidy Table Component with Virtual Scrolling Support
 * 
 * Virtual scrolling improves performance by only rendering visible rows in the DOM.
 * This is especially useful for large datasets (1000+ rows) where rendering all rows
 * would cause performance issues.
 * 
 * Usage:
 * <tidy-table 
 *   [enableVirtualScrolling]="true"
 *   [virtualScrollItemHeight]="40"
 *   [virtualScrollBuffer]="10"
 *   [preloadImages]="true"
 *   [dataSource]="myLargeDataset"
 *   [displayedColumns]="columns"
 *   [columnslabels]="columnLabels">
 * </tidy-table>
 * 
 * Virtual Scrolling Options:
 * - enableVirtualScrolling: Enable/disable virtual scrolling (default: false)
 * - virtualScrollItemHeight: Height of each row in pixels (default: 40)
 * - virtualScrollBuffer: Extra rows to render above/below viewport (default: 10)
 * 
 * Image Preloading Options:
 * - preloadImages: Enable/disable proactive image preloading (default: true)
 *   When true, the component will extract all image URLs from the entire dataSource
 *   and preload them immediately using JavaScript's Image API, regardless of whether
 *   the rows are currently rendered in the DOM. This eliminates loading delays when
 *   scrolling through the table, at the cost of increased initial bandwidth usage.
 *   Additionally, all rendered tidy-image components will use eager loading.
 * 
 * Performance Benefits:
 * - Only renders visible rows + buffer instead of all rows
 * - Smooth scrolling even with large datasets
 * - Reduced memory usage and faster initial load
 * - Proactively preloaded images eliminate render delays during scrolling
 * - Images are cached by the browser for instant display
 * 
 * Notes:
 * - Virtual scrolling is disabled by default for compatibility
 * - All rows should have consistent height for best results
 * - Selection works with visible rows only when virtual scrolling is enabled
 * - Image preloading is enabled by default for better UX
 * - Preloading scans the entire dataSource for images, not just visible rows
 */
@Component({
    templateUrl: 'table.component.html',
    selector: 'tidy-table',
    styleUrls: ['table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class TableComponent implements AfterViewInit, OnChanges, OnDestroy {

  Array = Array; // Make Array available in template

  // One-shot entrance (desktop): rows cascade in the first time a table shows
  // on a given page this session (keyed by URL, so revisits stay still even
  // when the host component is recreated). The class is removed once the
  // cascade finishes so filters/appends/sorts never re-animate rows.
  @HostBinding('class.page-entrance') entranceActive = false;
  private static entrancePlayedAt = new Map<string, number>();
  private entrancePlayed = false;

  /**
   * Renders a skeleton table (header labels from `columnslabels` + grey
   * placeholder rows) instead of the data table. The skeleton rows float in
   * with the same cascade the page-entrance plays, so pair it with
   * `skipEntrance` on the data table that replaces it — the real rows then
   * display instantly and the swap reads as the skeleton becoming the table.
   */
  @Input() skeletonLoading = false;
  @Input() skeletonRowCount = 50;
  /** Suppress the one-shot row entrance (use when a skeleton already played it). */
  @Input() skipEntrance = false;
  skeletonRows: number[] = Array.from({ length: 50 }, (_, i) => i);
  @Input() rowsToShow: number;
  @Input() totalRows: any;
  @Input() displayedColumns: string[] = [];
  @Input() columnslabels: string[] = [];
  @Input() minColumnWidth: boolean;
  @Input() enableVirtualScrolling = true; // New input for virtual scrolling
  @Input() virtualScrollItemHeight = 40; // Height of each row in pixels
  @Input() virtualScrollBuffer = 40; // Extra rows to render above/below viewport
  @Output() sortChange: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() selectedRowsChange = new EventEmitter<any>();
  @Output() unselectedRowsChange = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() cellClick = new EventEmitter<{column: string, element: any}>();
  @Output() toggleChange = new EventEmitter<{checked: boolean, column: string, element: any}>();
  @Output() selectChange = new EventEmitter<{value: string, column: string, element: any}>();
  @Output() moreItemsClick = new EventEmitter<{column: string, element: any, items: any[]}>();
  @Input() headerIcons: { [columnName: string]: string } = {};
  @Output() headerIconClick = new EventEmitter<string>();
  @Input() showPaginator = true;
  @Input() showColumnLines = true;
  @Input() allRowIsClickable = false;
  @Input() dataSource: any[];
  @Input() infiniteScroll = false;
  @Input() isLoading = false;
  @Input() showArrowIcon = true;
  @Input() scrollWithPage = false;
  @Input() hideHeaderCheckbox = false;
  @Input() preloadImages = true; // New input to control image preloading
  @Output() loadMore = new EventEmitter<void>();
  @Input() blockSorting = false;
  @Input() didLoadAllRows = false;
  tabledataSource: MatTableDataSource<any>;
  selection = new SelectionModel<any>(true, []);

  // Image preloading
  private preloadedImages = new Set<string>(); // Track preloaded image URLs

  // Virtual scrolling properties
  virtualScrollData: any[] = [];
  virtualScrollStartIndex = 0;
  virtualScrollEndIndex = 0;
  virtualScrollViewportHeight = 0;
  virtualScrollTotalHeight = 0;
  virtualScrollOffsetY = 0;
  private virtualScrollThrottleTimeout: any = null;
  private lastScrollTime: number | null = null;

  editingCell: { row: any; column: string } | null = null;
  editValue = '';
  @ViewChild('editInput') editInput: ElementRef;
  private originalValue = '';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('tableWrapper') tableWrapper: ElementRef;
  private scrollThreshold = 100; // pixels from bottom to trigger load more
  private ionContent: HTMLElement;

  @ViewChild(IonInfiniteScroll) infiniteScrollEl: IonInfiniteScroll;

  expandedArrayCells = new Set<string>();

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private document: Document,
    public windowService: WindowService,
    private util: Util,
    private storage: TidyStorage,
    private cdr: ChangeDetectorRef
  ) {}

  private maybePlayEntrance(): void {
    if (this.skipEntrance || this.entrancePlayed || !this.windowService?.isDesktopRes) {
      return;
    }
    this.entrancePlayed = true;
    const key = window.location.pathname + window.location.search;
    const playedAt = TableComponent.entrancePlayedAt.get(key);
    const now = Date.now();
    // A claimed key means this page already played its entrance this session —
    // except within a short burst window, so sibling tables that load together
    // on the same page all cascade in
    if (playedAt && now - playedAt > 4000) {
      return;
    }
    if (!playedAt) {
      TableComponent.entrancePlayedAt.set(key, now);
    }
    this.entranceActive = true;
    setTimeout(() => {
      this.entranceActive = false;
      this.cdr.markForCheck();
    }, 2000);
  }

  @HostListener('window:resize')
  onResize() {
    if (!this.scrollWithPage) {
      this.setTableHeight();
    }
    this.setupTableForMobile();
    // Add responsive column sizing on resize
    this.adjustColumnWidths();
    // Recalculate virtual scroll data after resize (needed for route reattachment)
    if (this.enableVirtualScrolling && this.dataSource) {
      this.calculateVirtualScrollData();
      if (this.tabledataSource) {
        this.tabledataSource.data = this.virtualScrollData;
        this.cdr.detectChanges();
      }
    }
  }

  private adjustColumnWidths() {
    if (!this.tableWrapper) {return;}
    
    const wrapper = this.tableWrapper.nativeElement;
    const table = wrapper.querySelector('table');
    const isMobile = !this.windowService.isDesktopRes;
    
    if (table) {
      // Remove any fixed widths to allow natural resizing
      const allCells = table.querySelectorAll('th, td');
      allCells.forEach((cell: HTMLElement) => {
        // Remove fixed widths but keep the last column fixed
        if (!cell.matches(':last-child')) {
          cell.style.width = 'auto';
          cell.style.minWidth = isMobile ? '100px' : '60px'; // Respect mobile minimum width
        }
      });
      
      // Ensure table uses full width and proper overflow behavior
      table.style.width = '100%';
      table.style.tableLayout = 'auto';
      wrapper.style.overflowX = isMobile ? 'auto' : 'hidden'; // Allow horizontal scroll on mobile
    }
  }

  private setupTableForMobile() {
    if (!this.tableWrapper) {return;}

    // Add a class to the table wrapper based on mobile/desktop view
    const isMobile = !this.windowService.isDesktopRes;
    const element = this.tableWrapper.nativeElement;

    if (isMobile) {
      element.classList.add('mobile-view');
    } else {
      element.classList.remove('mobile-view');
    }

    // Simple setup without complex width manipulation
    setTimeout(() => {
      if (this.tableWrapper) {
        const table = this.tableWrapper.nativeElement.querySelector('table');
        if (table) {
          // Set wrapper to allow horizontal scroll on mobile
          const wrapper = this.tableWrapper.nativeElement;
          wrapper.style.overflowX = isMobile ? 'auto' : 'hidden';
          wrapper.style.display = 'block';
          wrapper.style.width = '100%';

          // Remove sticky positioning and set minimum widths
          const allCells = table.querySelectorAll('th, td');
          allCells.forEach((cell: HTMLElement) => {
            cell.style.position = 'static';
            cell.style.left = 'auto';
            cell.style.backgroundColor = '';
            cell.style.zIndex = 'auto';
            cell.style.boxShadow = 'none';
            
            // Set 100px minimum width for all columns except last on mobile
            if (!cell.matches(':last-child')) {
              cell.style.minWidth = isMobile ? '100px' : '60px';
              cell.style.width = 'auto';
            }
          });

          // Allow text wrapping in cell content
          const textElements = table.querySelectorAll('.cell-text, span');
          textElements.forEach((el: HTMLElement) => {
            el.style.whiteSpace = 'normal';
            el.style.overflow = 'hidden';
            el.style.textOverflow = 'ellipsis';
          });

          // Remove sticky first column to allow natural flow
          const firstHeaderCells = table.querySelectorAll('th:first-child');
          const firstDataCells = table.querySelectorAll('td:first-child');

          [...firstHeaderCells, ...firstDataCells].forEach((cell: HTMLElement) => {
            cell.style.position = 'static';
            cell.style.left = 'auto';
            cell.style.backgroundColor = '';
            cell.style.zIndex = 'auto';
            cell.style.boxShadow = 'none';
          });
        }
      }
    }, 50);
  }

  private findIonContent() {
    if (!this.scrollWithPage) {return;}

    let element = this.tableWrapper.nativeElement;
    while (element && !element.classList.contains('ion-content')) {
      element = element.parentElement;
    }
    this.ionContent = element;

    if (this.ionContent) {
      this.ionContent.addEventListener('ionScroll', this.onIonScroll.bind(this));
    }
  }

  private onIonScroll(event) {
    if (!this.infiniteScroll || !this.scrollWithPage || this.isLoading) {return;}

    // Check if we've loaded all rows
    if (this.dataSource && ((this.totalRows && this.dataSource.length >= this.totalRows) || this.didLoadAllRows)) {
      return;
    }

    const scrollElement = event.target;
    const scrollPosition = scrollElement.scrollTop;
    const scrollHeight = scrollElement.scrollHeight;
    const clientHeight = scrollElement.clientHeight;

    if (scrollHeight - (scrollPosition + clientHeight) <= this.scrollThreshold) {
      this.pageChange.emit();
    }
  }

  onIonInfinite(event) {
    if (!this.infiniteScroll || this.isLoading) {
      event.target.complete();
      return;
    }

    // Check if we've loaded all rows
    if (this.dataSource && this.totalRows && this.dataSource.length >= this.totalRows) {
      event.target.complete();
      return;
    }

    this.pageChange.emit();
    event.target.complete();
  }

  private setTableHeight() {
    if (!this.tableWrapper) {return;}

    const element = this.tableWrapper.nativeElement;
    const rect = element.getBoundingClientRect();
    const viewportHeight = this.document.documentElement.clientHeight || this.document.body.clientHeight;
    const bottomPadding = 20; // Add some padding at the bottom

    // Calculate available height (viewport height minus distance from top of table to top of viewport)
    const availableHeight = viewportHeight - rect.top - bottomPadding;

    // Set the max-height
    element.style.maxHeight = `${availableHeight}px`;
  }

  ngAfterViewInit(): void {
    // The skeleton scrolls within the table area exactly like the data table:
    // same #tableWrapper ref, same viewport-height clamp.
    if (this.skeletonLoading && !this.scrollWithPage) {
      requestAnimationFrame(() => this.setTableHeight());
    }
    if (this.dataSource) {
      // Hide table initially if on mobile
      if (!this.windowService.isDesktopRes && this.tableWrapper) {
        this.tableWrapper.nativeElement.style.visibility = 'hidden';
      }

      // Preload all images from the data source
      this.preloadAllImages();

      // Initialize virtual scrolling if enabled
      if (this.enableVirtualScrolling) {
        // Ensure we have initial data rendered
        this.calculateVirtualScrollData();
        // If no data was calculated, show first batch
        if (this.virtualScrollData.length === 0 && this.dataSource.length > 0) {
          this.virtualScrollData = this.dataSource.slice(0, Math.min(50, this.dataSource.length));
          this.virtualScrollStartIndex = 0;
          this.virtualScrollEndIndex = this.virtualScrollData.length;
          this.virtualScrollOffsetY = 0;
        }
        this.tabledataSource = new MatTableDataSource(this.virtualScrollData);
      } else {
        this.tabledataSource = new MatTableDataSource(this.dataSource);
      }
      
      this.tabledataSource.sort = this.sort;
      if (!this.infiniteScroll) {
        this.tabledataSource.paginator = this.paginator;
      }

      // Setup table for mobile view
      setTimeout(() => {
        this.setupTableForMobile();

        // Force table to be scrollable on mobile
        if (!this.windowService.isDesktopRes && this.tableWrapper) {
          const wrapper = this.tableWrapper.nativeElement;
          const table = wrapper.querySelector('table');

          if (table) {
            // Ensure table has proper width and scrolling behavior
            table.style.tableLayout = 'auto';
            table.style.width = '100%';
            table.style.minWidth = 'max-content'; // Ensure table expands to fit all columns

            // Make cells respect minimum width
            const cells = table.querySelectorAll('th, td');
            cells.forEach((cell: HTMLElement) => {
              if (!cell.matches(':last-child')) {
                cell.style.minWidth = '100px';
                cell.style.whiteSpace = 'normal'; // Allow text wrapping
              }
            });

            // Allow text wrapping in cell content
            const textElements = table.querySelectorAll('.cell-text, span');
            textElements.forEach((el: HTMLElement) => {
              el.style.whiteSpace = 'normal';
              el.style.overflow = 'hidden';
              el.style.textOverflow = 'ellipsis';
            });

            // Enable horizontal scrolling in wrapper
            wrapper.style.overflowX = 'auto';
            wrapper.style.display = 'block';
            wrapper.style.width = '100%';

            // Remove sticky positioning from first column
            const firstHeaderCells = table.querySelectorAll('th:first-child');
            const firstDataCells = table.querySelectorAll('td:first-child');

            [...firstHeaderCells, ...firstDataCells].forEach((cell: HTMLElement) => {
              cell.style.position = 'static';
              cell.style.left = 'auto';
              cell.style.backgroundColor = '';
              cell.style.zIndex = 'auto';
              cell.style.boxShadow = 'none';
            });

            // Show table after all styling is complete
            wrapper.style.visibility = 'visible';
          }
        }
      });

      // Calculate and set initial height only if not scrolling with page
      if (!this.scrollWithPage) {
        requestAnimationFrame(() => {
          this.setTableHeight();

          // Remove fixed column width locking to allow responsive behavior
          const table = this.tableWrapper?.nativeElement?.querySelector('table');
          if (table) {
            // Instead of locking widths, ensure responsive behavior
            const headerCells = table.querySelectorAll('th');
            headerCells.forEach((cell: HTMLElement, _index) => {
              // Don't lock widths, allow natural responsive behavior
              if (!cell.matches(':last-child')) {
                cell.style.width = 'auto';
                cell.style.minWidth = '60px';
              }
            });

            // Remove fixed widths from data cells too
            this.displayedColumns.forEach((_, index) => {
              const cells = table.querySelectorAll(`td:nth-child(${index + 1})`);
              cells.forEach((cell: HTMLElement) => {
                if (!cell.matches(':last-child')) {
                  cell.style.width = 'auto';
                  cell.style.minWidth = '60px';
                }
              });
            });
          }
        });
      }
    }
    if (this.paginator && !this.infiniteScroll) {
      this.paginator.length = this.totalRows;
    }
    this.showColumnLines = false;
    if (this.showColumnLines) {
      this.document.documentElement.style.setProperty('--table-border-right', '1px solid rgba(0, 0, 0, .2)');
    } else {
      this.document.documentElement.style.setProperty('--table-border-right', 'none');
    }
    
    // Setup scroll listeners
    if (this.tableWrapper) {
      this.tableWrapper.nativeElement.addEventListener('scroll', (event: Event) => {
        // Handle virtual scrolling with immediate response for fast scrolling
        if (this.enableVirtualScrolling) {
          // Get current scroll position
          const scrollTop = this.tableWrapper.nativeElement.scrollTop;
          const maxScroll = this.tableWrapper.nativeElement.scrollHeight - this.tableWrapper.nativeElement.clientHeight;
          
          // If scrolling near the edges or very fast, update immediately
          if (scrollTop <= 100 || scrollTop >= maxScroll - 100) {
            this.calculateVirtualScrollData();
            if (this.tabledataSource) {
              this.tabledataSource.data = this.virtualScrollData;
              this.cdr.detectChanges();
            }
          } else {
            this.onVirtualScroll();
          }
        }
        
        // Handle infinite scroll
        if (this.infiniteScroll && !this.scrollWithPage && !this.isLoading) {
          // Check if we've loaded all rows
          if (this.dataSource && this.totalRows && this.dataSource.length >= this.totalRows) {
            return;
          }

          const element = event.target as HTMLElement;
          const scrollPosition = element.scrollTop + element.clientHeight;
          const scrollHeight = element.scrollHeight;

          if (scrollHeight - scrollPosition <= this.scrollThreshold) {
            this.pageChange.emit();
          }
        }
      });
    }

    // Find and setup ion-content scroll listener
    if (this.scrollWithPage) {
      setTimeout(() => this.findIonContent(), 0);
    }
    // Ensure view updates after all setup (fixes OnPush/Material table not rendering after hard refresh)
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.ionContent) {
      this.ionContent.removeEventListener('ionScroll', this.onIonScroll.bind(this));
    }

    // Clean up virtual scroll timeout
    if (this.virtualScrollThrottleTimeout) {
      clearTimeout(this.virtualScrollThrottleTimeout);
    }

  }

  ngOnChanges(changes) {
    if (changes.skeletonRowCount) {
      this.skeletonRows = Array.from({ length: this.skeletonRowCount }, (_, i) => i);
    }
    if (changes.dataSource && changes.dataSource.currentValue?.length) {
      this.maybePlayEntrance();
    }
    if (changes.dataSource && this.tabledataSource) {
      // Preload all images from the updated data source
      this.preloadAllImages();

      const wrapper = this.tableWrapper?.nativeElement;
      const isAppendOperation = changes.dataSource.previousValue &&
                               changes.dataSource.currentValue?.length > changes.dataSource.previousValue.length;

      if (isAppendOperation && wrapper) {
        // Store the current first visible row's position
        const _firstVisibleRowIndex = Math.floor(wrapper.scrollTop / this.virtualScrollItemHeight);
        const currentScrollTop = wrapper.scrollTop;

        // Update data
        if (this.enableVirtualScrolling) {
          this.calculateVirtualScrollData();
          // Ensure we always have some data to show
          if (this.virtualScrollData.length === 0 && changes.dataSource.currentValue?.length > 0) {
            this.virtualScrollData = changes.dataSource.currentValue.slice(0, Math.min(50, changes.dataSource.currentValue.length));
            this.virtualScrollStartIndex = 0;
            this.virtualScrollEndIndex = this.virtualScrollData.length;
            this.virtualScrollOffsetY = 0;
          }
          this.tabledataSource = new MatTableDataSource(this.virtualScrollData);
        } else {
          this.tabledataSource = new MatTableDataSource(changes.dataSource.currentValue);
        }
        this.tabledataSource.sort = this.sort;
        this.tabledataSource.paginator = this.paginator;

        // Restore scroll to keep the same content visible
        requestAnimationFrame(() => {
          wrapper.scrollTop = currentScrollTop;
        });
      } else {
        // Normal update without scroll preservation
        if (this.enableVirtualScrolling) {
          this.calculateVirtualScrollData();
          // Ensure we always have some data to show
          if (this.virtualScrollData.length === 0 && changes.dataSource.currentValue?.length > 0) {
            this.virtualScrollData = changes.dataSource.currentValue.slice(0, Math.min(50, changes.dataSource.currentValue.length));
            this.virtualScrollStartIndex = 0;
            this.virtualScrollEndIndex = this.virtualScrollData.length;
            this.virtualScrollOffsetY = 0;
          }
          this.tabledataSource = new MatTableDataSource(this.virtualScrollData);
        } else {
          this.tabledataSource = new MatTableDataSource(changes.dataSource.currentValue);
        }
        this.tabledataSource.sort = this.sort;
        this.tabledataSource.paginator = this.paginator;
      }
    } else if (this.dataSource && this.tabledataSource) {
      // Handle case where dataSource array reference hasn't changed but content might have (like sorting in place)
      // This happens when parent component sorts the same array reference
      if (this.enableVirtualScrolling) {
        this.calculateVirtualScrollData();
        if (this.tabledataSource) {
          this.tabledataSource.data = this.virtualScrollData;
        }
      } else {
        this.tabledataSource.data = this.dataSource;
      }
    }
    
    // Handle preloadImages input changes
    if (changes.preloadImages && changes.preloadImages.currentValue && this.dataSource) {
      this.preloadAllImages();
    }
    
    if (changes.totalRows && this.paginator) {
      this.paginator.length = this.totalRows;
      this.paginator.pageIndex = 0;
      this.paginator.pageSize = this.rowsToShow;
      this.paginator.length = this.totalRows;
      this.tabledataSource.paginator = this.paginator;
    }
  }

  emitSortChange(sort) {
    if (this.blockSorting) {
      return;
    }
    this.sortChange.emit(sort);
    
    // After emitting sort change, refresh virtual scrolling data to show the new sorted order
    // We need to wait for the parent component to process the sort change
    setTimeout(() => {
      if (this.enableVirtualScrolling) {
        this.calculateVirtualScrollData();
        if (this.tabledataSource) {
          this.tabledataSource.data = this.virtualScrollData;
          this.cdr.detectChanges();
        }
      }
    }, 0);
  }

  onEditKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.saveEdit();
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
    if (
      ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', '.', 'ArrowLeft', 'ArrowRight'].indexOf(event.key) !== -1 ||
      (event.ctrlKey === true && ['a', 'c', 'v', 'x'].indexOf(event.key.toLowerCase()) !== -1)
    ) {
      return;
    }
    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  saveEdit() {
    if (this.editingCell) {
      const { row, column } = this.editingCell;
      const editValue = this.editValue;
      const originalValue = this.originalValue;
      // Closed before onEdit runs, so a handler that redraws the table sees it closed.
      this.editingCell = null;
      this.editValue = '';
      this.originalValue = '';
      // A number input hands ngModel null once its text is erased; that is no edit, not a 0.
      if (editValue !== '' && editValue !== null && editValue !== originalValue && !isNaN(Number(editValue))) {
        localStorage.setItem('tableInput', editValue);
        row[column].value = editValue;

        // Check for quantity issues after edit
        const currentQuantity = column === 'Current #' ? Number(editValue) : Number(row['Current #'].value);
        const minQuantity = column === 'Min #' ? Number(editValue) : Number(row['Min #'].value);

        // Update icon if current quantity is less than min quantity
        if (currentQuantity < minQuantity) {
          row['Current #'].icon = 'assets/img/request-failed.svg';
        } else {
          delete row['Current #'].icon;
        }

        if (row[column].onEdit) {
          row[column].onEdit(Number(editValue));
        }
      } else {
        row[column].value = originalValue;
      }
    }
  }

  cancelEdit() {
    this.editingCell = null;
    this.editValue = '';
  }

  isEditing(row: any, column: string): boolean {
    return this.editingCell?.row === row && this.editingCell?.column === column;
  }

  treatJsonData(rawData, headers) {
    const csvJson = rawData.map((row) => {
      const object: any = {};
      headers.forEach((field_name) => {
        // Cells that render HTML (e.g. status chips) provide a plain-text
        // exportValue so markup never leaks into the CSV.
        if (row[field_name].exportValue !== undefined) {
          object[field_name] = row[field_name].exportValue;
        } else if (row[field_name].value) {
          object[field_name] = row[field_name].value;
        } else {
          object[field_name] = '';
        }
      });
      return object;
    });
    return csvJson;
  }

  exportCsv(tabledataSource){
    if (tabledataSource.filteredData.length === 0) {
      return this.util.showError('No data to export', 5000);
    }
    const filteredHeaders = this.displayedColumns.filter(header => header !== 'last-column' && header !== ' ');
    const dataToConvert = {
      data: this.treatJsonData(tabledataSource.filteredData, filteredHeaders),
      filename: 'TIDY-export',
      delimiter: ',',
      headers: filteredHeaders
    }
    csvDownload(dataToConvert);
  }

  // A fresh SafeHtml wrapper on every change detection reads as a new value to the
  // [innerHTML] binding, so every cell's HTML was re-set on every cycle. The same
  // string now maps to the same wrapper.
  private readonly sanitizedHtml = new Map<unknown, SafeHtml>();

  sanitize(html: string): SafeHtml {
    let safe = this.sanitizedHtml.get(html);
    if (!safe) {
      if (this.sanitizedHtml.size >= SANITIZED_HTML_CACHE_MAX) { this.sanitizedHtml.clear(); }
      safe = this.sanitizer.bypassSecurityTrustHtml(html);
      this.sanitizedHtml.set(html, safe);
    }
    return safe;
  }

  getTruncatedText(text: string, shouldTruncate = false): string {
    if (!shouldTruncate || !text || text.length <= 50) {
      return text;
    }
    return `${text.substring(0, 50)}...`;
  }


  isAllSelected(): boolean {
    const numSelected = this.selection?.selected?.length;
    const numRows = this.tabledataSource?.data?.length;
    return numSelected === numRows;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.tabledataSource?.data);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  selectPage(page) {
    this.pageChange.emit(page);
  }

  emitSelectionChange() {
    this.selectedRowsChange.emit(this.selection?.selected)
    const unselectedRows = this.tabledataSource?.data?.filter((row) => !this.selection?.selected?.includes(row))
    this.unselectedRowsChange.emit(unselectedRows)
  }

  handleCellClick(column: string, element: any, _event: Event) {
    this.cellClick.emit({column, element});
  }

  handleSpanClick(column: string, element: any, event: Event) {
    if (element[column]?.editable) {
      this.editingCell = { row: element, column };
      this.originalValue = element[column].value?.toString() || '';
      this.editValue = '';
      setTimeout(() => {
        if (this.editInput?.nativeElement) {
          this.editInput.nativeElement.focus();
        }
      });
      event.stopPropagation();
    } else if (element[column]?.action) {
      element[column]?.action();
      event.stopPropagation();
    }
  }

  // Items of an array cell can carry their own `action` (e.g. the job and the reservation
  // behind a transaction); clicking one opens its record instead of the row.
  onArrayItemClick(item: any, event: Event): void {
    if (typeof item?.action !== 'function') { return; }
    event.stopPropagation();
    item.action();
  }

  // Cells can carry `buttons: [{ text, image?, colorImage?, initials?, primary?, disabled?, action }]` to render
  // inline tidy-small-buttons (e.g. approve/decline actions) instead of a value.
  onCellButtonClick(button: any) {
    if (button?.disabled) {
      return;
    }
    if (typeof button?.action === 'function') {
      button.action();
    }
  }

  onToggleChange(checked: boolean, column: string, element: any) {
    element[column].toggledOn = checked;
    if (element[column].action) {
      element[column].action();
    }
    this.toggleChange.emit({ checked, column, element });
  }

  onSelectChange(value: string, column: string, element: any) {
    element[column].value = value;
    this.selectChange.emit({ value, column, element });
  }

  // Inline selects with more options than this get a search box, like tidy-select.
  readonly inlineSelectSearchMin = 5;
  /** Typed search per inline select, keyed by row id + column. Cleared when the panel closes. */
  inlineSelectSearch: Record<string, string> = {};

  onInlineSelectSearch(event: Event, column: string, element: any) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.inlineSelectSearch = { ...this.inlineSelectSearch, [`${element?.id}|${column}`]: value };
  }

  onInlineSelectOpened(opened: boolean, column: string, element: any) {
    if (opened) { return; }
    const key = `${element?.id}|${column}`;
    if (this.inlineSelectSearch[key]) {
      const next = { ...this.inlineSelectSearch };
      delete next[key];
      this.inlineSelectSearch = next;
    }
  }

  onHeaderIconClick(column: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.headerIconClick.emit(column);
  }

  onMoreItemsClick(column: string, element: any, event: Event) {
    event.stopPropagation();
    if (Array.isArray(element[column])) {
      this.moreItemsClick.emit({ column, element, items: element[column] });
    }
  }

  clearSelection() {
    this.selection.clear();
    this.selectedRowsChange.emit([]);
    this.unselectedRowsChange.emit(this.tabledataSource?.data);
  }

  getArrayCellKey(element: any, column: string): string {
    // Use row index in data source for more reliable unique keys
    const rowIndex = this.tabledataSource?.data?.indexOf(element);
    return `${rowIndex}_${column}`;
  }

  isArrayCellExpanded(element: any, column: string): boolean {
    return this.expandedArrayCells.has(this.getArrayCellKey(element, column));
  }

  toggleArrayCellExpand(element: any, column: string, event: Event): void {
    event.stopPropagation();
    const key = this.getArrayCellKey(element, column);
    if (this.expandedArrayCells.has(key)) {
      this.expandedArrayCells.delete(key);
    } else {
      this.expandedArrayCells.add(key);
    }
  }

  private calculateVirtualScrollData() {
    if (!this.enableVirtualScrolling || !this.dataSource || !this.tableWrapper) {
      this.virtualScrollData = this.dataSource || [];
      return;
    }

    const containerHeight = this.tableWrapper.nativeElement.clientHeight;
    const scrollTop = this.tableWrapper.nativeElement.scrollTop;
    const totalItems = this.dataSource.length;
    
    // Calculate viewport
    this.virtualScrollViewportHeight = containerHeight;
    this.virtualScrollTotalHeight = totalItems * this.virtualScrollItemHeight;
    
    // Calculate visible range with more conservative approach
    const startIndex = Math.floor(scrollTop / this.virtualScrollItemHeight);
    const visibleRows = containerHeight > 0 ? Math.ceil(containerHeight / this.virtualScrollItemHeight) : 25;
    
    // Apply buffer more aggressively to prevent gaps during fast scrolling
    const bufferStart = Math.max(0, startIndex - this.virtualScrollBuffer);
    const bufferEnd = Math.min(totalItems, startIndex + visibleRows + this.virtualScrollBuffer);
    
    // Ensure we always have a minimum number of rows rendered
    const minRowsToRender = Math.min(50, totalItems);
    const actualStart = Math.max(0, Math.min(bufferStart, totalItems - minRowsToRender));
    const actualEnd = Math.min(totalItems, Math.max(bufferEnd, actualStart + minRowsToRender));
    
    this.virtualScrollStartIndex = actualStart;
    this.virtualScrollEndIndex = actualEnd;
    this.virtualScrollOffsetY = this.virtualScrollStartIndex * this.virtualScrollItemHeight;

    // Store current column widths before updating data
    const table = this.tableWrapper.nativeElement.querySelector('table');
    if (table) {
      const headerCells = Array.from(table.querySelectorAll('th')) as HTMLElement[];
      const widths = headerCells.map(cell => cell.offsetWidth);

      // Update data
      this.virtualScrollData = this.dataSource.slice(this.virtualScrollStartIndex, this.virtualScrollEndIndex);

      // Restore column widths after data update
      requestAnimationFrame(() => {
        const newHeaderCells = Array.from(table.querySelectorAll('th')) as HTMLElement[];
        newHeaderCells.forEach((cell, index) => {
          if (widths[index]) {
            cell.style.width = `${widths[index]}px`;
          }
        });
      });
    } else {
      this.virtualScrollData = this.dataSource.slice(this.virtualScrollStartIndex, this.virtualScrollEndIndex);
    }
}

  private onVirtualScroll() {
    if (!this.enableVirtualScrolling) {return;}
    
    // For fast scrolling, update immediately without throttling
    const now = Date.now();
    if (!this.lastScrollTime || now - this.lastScrollTime > 100) {
      // If it's been more than 100ms since last scroll, update immediately
      this.calculateVirtualScrollData();
      if (this.tabledataSource) {
        this.tabledataSource.data = this.virtualScrollData;
        this.cdr.detectChanges();
      }
      this.lastScrollTime = now;
    } else {
      // For rapid scrolling, use reduced throttling
      if (this.virtualScrollThrottleTimeout) {
        clearTimeout(this.virtualScrollThrottleTimeout);
      }
      
      this.virtualScrollThrottleTimeout = setTimeout(() => {
        this.calculateVirtualScrollData();
        
        // Update MatTableDataSource with visible data only
        if (this.tabledataSource) {
          this.tabledataSource.data = this.virtualScrollData;
          // Trigger change detection since we're using OnPush
          this.cdr.detectChanges();
        }
        this.lastScrollTime = Date.now();
      }, 8); // Reduced from 16ms to 8ms for faster response
    }
  }

  // Add a method to refresh virtual scrolling when needed
  refreshVirtualScrolling(): void {
    if (this.enableVirtualScrolling) {
      this.calculateVirtualScrollData();
      if (this.tabledataSource) {
        this.tabledataSource.data = this.virtualScrollData;
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Public method to refresh table data - useful when parent component modifies the data array
   * without changing the array reference (like sorting in place)
   */
  refreshTableData(): void {
    if (this.enableVirtualScrolling) {
      this.calculateVirtualScrollData();
      if (this.tabledataSource) {
        this.tabledataSource.data = this.virtualScrollData;
      }
    } else {
      if (this.tabledataSource && this.dataSource) {
        this.tabledataSource.data = this.dataSource;
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Extract all image URLs from the data source
   */
  private extractImageUrls(): string[] {
    if (!this.dataSource || !this.preloadImages) {
      return [];
    }

    const imageUrls = new Set<string>();

    // Add static chevron icon if showArrowIcon is true
    if (this.showArrowIcon) {
      imageUrls.add('assets/img/chevron-right.svg');
    }

    // Extract images from all rows and columns
    this.dataSource.forEach(row => {
      this.displayedColumns.forEach(column => {
        if (row[column] && typeof row[column] === 'object') {
          // Handle regular cell icons
          if (row[column].icon) {
            imageUrls.add(row[column].icon);
          }

          // Handle array cells
          if (Array.isArray(row[column])) {
            row[column].forEach(item => {
              if (item && typeof item === 'object' && item.icon) {
                imageUrls.add(item.icon);
              }
            });
          }
        }
      });
    });

    return Array.from(imageUrls);
  }

  /**
   * Preload all images from the data source
   */
  private preloadAllImages(): void {
    if (!this.preloadImages) {
      return;
    }

    const imageUrls = this.extractImageUrls();
    
    if (imageUrls.length === 0) {
      return;
    }
    
    imageUrls.forEach(url => {
      // Skip if already preloaded
      if (this.preloadedImages.has(url)) {
        return;
      }

      // Skip empty or invalid URLs
      if (!url || typeof url !== 'string') {
        return;
      }

      // Create new Image object to trigger preload
      const img = new Image();
      img.onload = () => {
        this.preloadedImages.add(url);
      };
      img.onerror = () => {
        this.preloadedImages.add(url);
      };
      img.src = url;
    });
  }
}

